import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Settings } from 'lucide-react';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      loadScripts(savedPreferences);
    }
  }, []);

  const loadScripts = (prefs) => {
    // Load analytics scripts if consent given
    if (prefs.analytics) {
      // Example: Load Google Analytics
      console.log('Loading analytics scripts');
    }
    
    // Load marketing scripts if consent given
    if (prefs.marketing) {
      // Example: Load marketing pixels
      console.log('Loading marketing scripts');
    }
    
    // Load functional scripts if consent given
    if (prefs.functional) {
      // Example: Load chat widgets, maps, etc.
      console.log('Loading functional scripts');
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    loadScripts(allAccepted);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    loadScripts(onlyNecessary);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    loadScripts(preferences);
    setIsVisible(false);
  };

  const handlePreferenceChange = (category) => {
    if (category === 'necessary') return; // Cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
        aria-hidden="true"
      />
      
      {/* Cookie Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-white dark:bg-neutral-900 border-t-2 border-neutral-200 dark:border-neutral-700 shadow-2xl"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        aria-modal="true"
      >
        <div className="max-w-7xl mx-auto p-6">
          {!showDetails ? (
            // Simple Banner View
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie 
                  className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" 
                  aria-hidden="true"
                />
                <div>
                  <h2 
                    id="cookie-banner-title"
                    className="text-lg font-semibold text-neutral-900 dark:text-white mb-2"
                  >
                    Ihre Privatsphäre ist uns wichtig
                  </h2>
                  <p 
                    id="cookie-banner-description"
                    className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed"
                  >
                    Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzererfahrung zu bieten. 
                    Einige sind für den Betrieb der Website erforderlich, andere helfen uns, diese Website und Ihre Erfahrung zu verbessern.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setShowDetails(true)}
                  onKeyDown={(e) => handleKeyDown(e, () => setShowDetails(true))}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Cookie-Einstellungen anpassen"
                >
                  <Settings className="w-4 h-4" aria-hidden="true" />
                  Einstellungen
                </button>
                
                <button
                  onClick={handleRejectAll}
                  onKeyDown={(e) => handleKeyDown(e, handleRejectAll)}
                  className="px-6 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Nur erforderliche
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  onKeyDown={(e) => handleKeyDown(e, handleAcceptAll)}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          ) : (
            // Detailed Settings View
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                  Cookie-Einstellungen
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  onKeyDown={(e) => handleKeyDown(e, () => setShowDetails(false))}
                  className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Cookie-Einstellungen schließen"
                >
                  <X className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>

              <div className="grid gap-4">
                {/* Necessary Cookies */}
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-600" aria-hidden="true" />
                      <h3 className="font-medium text-neutral-900 dark:text-white">
                        Erforderliche Cookies
                      </h3>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-green-600 font-medium mr-2">Immer aktiv</span>
                      <div className="w-10 h-6 bg-green-600 rounded-full relative">
                        <div className="w-4 h-4 bg-white rounded-full absolute top-1 right-1"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" aria-hidden="true" />
                      <h3 className="font-medium text-neutral-900 dark:text-white">
                        Analyse-Cookies
                      </h3>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <span className="sr-only">Analyse-Cookies aktivieren/deaktivieren</span>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="sr-only"
                      />
                      <div 
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          preferences.analytics ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                        }`}
                        role="switch"
                        aria-checked={preferences.analytics}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, () => handlePreferenceChange('analytics'))}
                      >
                        <div 
                          className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${
                            preferences.analytics ? 'translate-x-4' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Helfen uns zu verstehen, wie Besucher mit der Website interagieren, um die Benutzererfahrung zu verbessern.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-purple-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">M</span>
                      </div>
                      <h3 className="font-medium text-neutral-900 dark:text-white">
                        Marketing-Cookies
                      </h3>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <span className="sr-only">Marketing-Cookies aktivieren/deaktivieren</span>
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="sr-only"
                      />
                      <div 
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          preferences.marketing ? 'bg-purple-600' : 'bg-neutral-300 dark:bg-neutral-600'
                        }`}
                        role="switch"
                        aria-checked={preferences.marketing}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, () => handlePreferenceChange('marketing'))}
                      >
                        <div 
                          className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${
                            preferences.marketing ? 'translate-x-4' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Werden verwendet, um Werbung relevanter für Sie und Ihre Interessen zu gestalten.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-orange-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">F</span>
                      </div>
                      <h3 className="font-medium text-neutral-900 dark:text-white">
                        Funktionale Cookies
                      </h3>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <span className="sr-only">Funktionale Cookies aktivieren/deaktivieren</span>
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handlePreferenceChange('functional')}
                        className="sr-only"
                      />
                      <div 
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          preferences.functional ? 'bg-orange-600' : 'bg-neutral-300 dark:bg-neutral-600'
                        }`}
                        role="switch"
                        aria-checked={preferences.functional}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, () => handlePreferenceChange('functional'))}
                      >
                        <div 
                          className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${
                            preferences.functional ? 'translate-x-4' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Ermöglichen erweiterte Funktionalitäten wie Chat-Widgets, Karten oder personalisierte Inhalte.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  onClick={handleRejectAll}
                  onKeyDown={(e) => handleKeyDown(e, handleRejectAll)}
                  className="px-6 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Alle ablehnen
                </button>
                
                <button
                  onClick={handleSavePreferences}
                  onKeyDown={(e) => handleKeyDown(e, handleSavePreferences)}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Auswahl speichern
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  onKeyDown={(e) => handleKeyDown(e, handleAcceptAll)}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Alle akzeptieren
                </button>
              </div>

              <div className="text-xs text-neutral-500 dark:text-neutral-400 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <p>
                  Weitere Informationen finden Sie in unserer{' '}
                  <a 
                    href="/datenschutz" 
                    className="text-blue-600 hover:text-blue-700 underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    Datenschutzerklärung
                  </a>
                  . Sie können Ihre Einstellungen jederzeit ändern.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
